<template>
  <div class="main">
    <!-- Левая колонка: поиск организации -->
    <div class="left">
      <div class="content">
        <p class="title">{{ $t("Добавление организации") }}</p>

        <div class="section">
          <p class="label">{{ $t("Введите БИН организации") }}</p>

          <div class="input-section">
            <div class="relative w-full">
              <Search
                class="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground"
              />
              <Input
                v-model="searchQuery"
                :placeholder="$t('Поиск')"
                class="pl-9"
                @input="onSearchInput"
              />
            </div>

            <Button variant="secondary" @click="searchOrganization">
              {{ $t("Запросить данные") }}
            </Button>
          </div>
        </div>
      </div>
    </div>

    <!-- Правая колонка: данные организации -->
    <div class="right">
      <div class="content">
        <SectionHeading
          :title="$t('Данные организации')"
          :description="$t('Заполните обязательные поля')"
        />

        <!-- TODO: временно скрыто — нет поддержки для загрузки логотипа -->
        <!-- Логотип -->
        <!-- <div class="right-block">
          <div class="flex items-center gap-4">
            <div class="blocked">
              <svg-icon name="huge-add-image" class="text-muted-foreground" />
            </div>
            <div class="flex flex-col justify-center items-start gap-1">
              <p class="title">Логотип</p>
              <p class="label">
                {{ $t("Поддерживаемые форматы") }}: jpeg {{ $t("или") }} png,
                {{ $t("до 800 KB") }}
              </p>
            </div>
          </div>
        </div> -->

        <!-- Основная форма -->
        <div class="section">
          <form class="w-full grid gap-4" novalidate @submit.prevent>
            <div class="grid grid-cols-2 gap-3">
              <FormItem float>
                <FormLabel
                  class="required"
                  :class="{ 'label-error': errors.name }"
                  >{{ $t("Наименование организации (рус)") }}</FormLabel
                >
                <FormControl>
                  <Input
                    v-model="form.name"
                    :class="{ 'input-error': errors.name }"
                    @input="clearError('name')"
                    @blur="validateField('name')"
                  />
                </FormControl>
              </FormItem>

              <FormItem float>
                <FormLabel
                  class="required"
                  :class="{ 'label-error': errors.name_kz }"
                  >{{ $t("Наименование организации (қаз)") }}</FormLabel
                >
                <FormControl>
                  <Input
                    v-model="form.name_kz"
                    :class="{ 'input-error': errors.name_kz }"
                    @input="clearError('name_kz')"
                    @blur="validateField('name_kz')"
                  />
                </FormControl>
              </FormItem>
            </div>

            <div class="grid grid-cols-2 gap-3">
              <FormItem float>
                <FormLabel
                  class="required"
                  :class="{ 'label-error': errors.short_name_ru }"
                  >{{ $t("Краткое наименование организации (рус)") }}</FormLabel
                >
                <FormControl>
                  <Input
                    v-model="form.short_name_ru"
                    :class="{ 'input-error': errors.short_name_ru }"
                    @input="clearError('short_name_ru')"
                    @blur="validateField('short_name_ru')"
                  />
                </FormControl>
              </FormItem>

              <FormItem float>
                <FormLabel
                  class="required"
                  :class="{ 'label-error': errors.short_name_kz }"
                  >{{ $t("Краткое наименование организации (қаз)") }}</FormLabel
                >
                <FormControl>
                  <Input
                    v-model="form.short_name_kz"
                    :class="{ 'input-error': errors.short_name_kz }"
                    @input="clearError('short_name_kz')"
                    @blur="validateField('short_name_kz')"
                  />
                </FormControl>
              </FormItem>
            </div>

            <FormItem float>
              <FormLabel
                class="required"
                :class="{ 'label-error': errors.bin }"
                >{{ $t("БИН организации") }}</FormLabel
              >
              <FormControl>
                <Input
                  v-model="form.bin"
                  :class="{ 'input-error': errors.bin }"
                  maxlength="12"
                  inputmode="numeric"
                  @input="onBinInput"
                  @blur="validateField('bin')"
                />
              </FormControl>
            </FormItem>

            <FormItem float>
              <FormLabel
                class="required"
                :class="{ 'label-error': errors.code }"
                >{{ $t("Код организации") }}</FormLabel
              >
              <FormControl>
                <Input
                  v-model="form.code"
                  :class="{ 'input-error': errors.code }"
                  maxlength="2"
                  inputmode="numeric"
                  @input="onCodeInput"
                  @blur="validateField('code')"
                />
              </FormControl>
            </FormItem>
          </form>
        </div>

        <!-- Данные руководителя и адрес -->
        <div class="section">
          <p class="info-label">{{ $t("Данные руководителя и адрес") }}</p>
          <form class="w-full grid gap-4" novalidate @submit.prevent>
            <FormItem float>
              <FormLabel
                class="required"
                :class="{ 'label-error': errors.head_full_name }"
                >{{ $t("ФИО руководителя организации") }}</FormLabel
              >
              <FormControl>
                <Input
                  v-model="form.head_full_name"
                  :class="{ 'input-error': errors.head_full_name }"
                  @input="clearError('head_full_name')"
                  @blur="validateField('head_full_name')"
                />
              </FormControl>
            </FormItem>

            <div class="grid grid-cols-2 gap-3">
              <FormItem float>
                <FormLabel>{{ $t("Должность руководителя (рус)") }}</FormLabel>
                <FormControl>
                  <Input v-model="form.head_position_ru" />
                </FormControl>
              </FormItem>

              <FormItem float>
                <FormLabel>{{ $t("Должность руководителя (қаз)") }}</FormLabel>
                <FormControl>
                  <Input v-model="form.head_position_kz" />
                </FormControl>
              </FormItem>
            </div>

            <div class="grid grid-cols-2 gap-3">
              <FormItem float>
                <FormLabel>{{ $t("Юридический адрес (рус)") }}</FormLabel>
                <FormControl>
                  <Textarea v-model="form.legal_address_ru" :rows="3" />
                </FormControl>
              </FormItem>

              <FormItem float>
                <FormLabel>{{ $t("Юридический адрес (қаз)") }}</FormLabel>
                <FormControl>
                  <Textarea v-model="form.legal_address_kz" :rows="3" />
                </FormControl>
              </FormItem>
            </div>
          </form>
        </div>

        <!-- Контактные данные -->
        <div class="section">
          <p class="info-label">{{ $t("Контактные данные") }}</p>
          <form class="w-full grid gap-4" novalidate @submit.prevent>
            <div class="grid grid-cols-3 gap-3">
              <FormItem float>
                <FormLabel
                  class="required"
                  :class="{ 'label-error': errors.phone }"
                  >{{ $t("Телефон") }}</FormLabel
                >
                <FormControl>
                  <Input
                    v-model="form.phone"
                    :class="{ 'input-error': errors.phone }"
                    placeholder="+7XXXXXXXXXX"
                    inputmode="tel"
                    maxlength="12"
                    @input="onPhoneInput"
                    @blur="validateField('phone')"
                  />
                </FormControl>
              </FormItem>

              <FormItem float>
                <FormLabel
                  class="required"
                  :class="{ 'label-error': errors.email }"
                  >{{ $t("E-mail") }}</FormLabel
                >
                <FormControl>
                  <Input
                    v-model="form.email"
                    :class="{ 'input-error': errors.email }"
                    type="email"
                    @input="clearError('email')"
                    @blur="validateField('email')"
                  />
                </FormControl>
              </FormItem>

              <FormItem float>
                <FormLabel :class="{ 'label-error': errors.website }">{{
                  $t("Сайт")
                }}</FormLabel>
                <FormControl>
                  <Input
                    v-model="form.website"
                    :class="{ 'input-error': errors.website }"
                    placeholder="https://example.com"
                    @input="clearError('website')"
                    @blur="validateField('website')"
                  />
                </FormControl>
              </FormItem>
            </div>

            <div class="grid grid-cols-2 gap-3">
              <FormItem float>
                <FormLabel>{{ $t("Рабочие время (с)") }}</FormLabel>
                <FormControl>
                  <Input v-model="form.work_time_from" type="time" />
                </FormControl>
              </FormItem>

              <FormItem float>
                <FormLabel>{{ $t("Рабочие время (по)") }}</FormLabel>
                <FormControl>
                  <Input v-model="form.work_time_to" type="time" />
                </FormControl>
              </FormItem>
            </div>
          </form>
        </div>

        <!-- Данные счета -->
        <div class="section">
          <p class="info-label">{{ $t("Данные счета") }}</p>
          <form class="w-full grid gap-4" novalidate @submit.prevent>
            <FormItem float>
              <FormLabel>{{ $t("Наименование банка") }}</FormLabel>
              <FormControl>
                <Input v-model="form.bank_name" />
              </FormControl>
            </FormItem>
            <FormItem float>
              <FormLabel :class="{ 'label-error': errors.iik }">{{
                $t("ИИК")
              }}</FormLabel>
              <FormControl>
                <Input
                  v-model="form.iik"
                  :class="{ 'input-error': errors.iik }"
                  maxlength="20"
                  @input="onIikInput"
                  @blur="validateField('iik')"
                />
              </FormControl>
            </FormItem>
            <FormItem float>
              <FormLabel>{{ $t("БИК") }}</FormLabel>
              <FormControl>
                <Input v-model="form.bik" />
              </FormControl>
            </FormItem>
            <FormItem float>
              <FormLabel>{{ $t("КБЕ") }}</FormLabel>
              <FormControl>
                <Input
                  v-model="form.kbe"
                  maxlength="2"
                  inputmode="numeric"
                  @input="onKbeInput"
                />
              </FormControl>
            </FormItem>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Search } from "lucide-vue-next";
import Button from "@/shared/ui/button/Button.vue";
import { SectionHeading } from "@/shared/ui/section-heading";
import { FormItem, FormLabel, FormControl } from "@/shared/ui/form";
import { Input } from "@/shared/ui/input";
import { Textarea } from "@/shared/ui/textarea";

const REQUIRED_FIELDS = [
  "name",
  "name_kz",
  "short_name_ru",
  "short_name_kz",
  "bin",
  "code",
  "head_full_name",
  "phone",
  "email",
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const URL_RE = /^https?:\/\/[^\s/$.?#].[^\s]*$/i;

export default {
  name: "OrganizationInfoLayout",
  components: {
    Button,
    SectionHeading,
    FormItem,
    FormLabel,
    FormControl,
    Input,
    Textarea,
    Search,
  },
  props: {
    form: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      searchQuery: "",
      errors: {},
    };
  },

  methods: {
    onSearchInput(event) {
      const value = event.target ? event.target.value : event;
      this.searchQuery = String(value).replace(/\D/g, "").slice(0, 12);
    },
    searchOrganization() {
      // TODO: интеграция с API поиска по БИН
    },
    onBinInput(event) {
      const value = event.target ? event.target.value : event;
      this.form.bin = String(value).replace(/\D/g, "").slice(0, 12);
      this.clearError("bin");
    },
    onCodeInput(event) {
      const value = event.target ? event.target.value : event;
      this.form.code = String(value).replace(/\D/g, "").slice(0, 2);
      this.clearError("code");
    },
    onKbeInput(event) {
      const value = event.target ? event.target.value : event;
      this.form.kbe = String(value).replace(/\D/g, "").slice(0, 2);
    },
    onIikInput(event) {
      const value = event.target ? event.target.value : event;
      this.form.iik = String(value)
        .replace(/[^A-Za-z0-9]/g, "")
        .toUpperCase()
        .slice(0, 20);
      this.clearError("iik");
    },
    onPhoneInput(event) {
      const value = event.target ? event.target.value : event;
      const digits = String(value).replace(/\D/g, "").slice(0, 11);
      let normalized = digits;
      if (normalized.startsWith("8")) {
        normalized = "7" + normalized.slice(1);
      }
      if (normalized && !normalized.startsWith("7")) {
        normalized = "7" + normalized;
      }
      this.form.phone = normalized ? "+" + normalized : "";
      this.clearError("phone");
    },
    clearError(field) {
      if (this.errors[field]) {
        delete this.errors[field];
      }
    },
    validateField(field) {
      const message = this.getFieldError(field);
      if (message) {
        this.errors = { ...this.errors, [field]: message };
      } else {
        this.clearError(field);
      }
      return !message;
    },
    getFieldError(field) {
      const value = this.form?.[field];
      const isEmpty = value === null || value === undefined || value === "";

      if (REQUIRED_FIELDS.includes(field) && isEmpty) {
        return this.$t("Обязательное поле");
      }
      if (isEmpty) return "";

      if (field === "bin") {
        if (!/^\d{12}$/.test(value)) {
          return this.$t("БИН должен содержать 12 цифр");
        }
      }
      if (field === "code") {
        if (!/^\d{1,2}$/.test(value)) {
          return this.$t("Код должен содержать 1-2 цифры");
        }
      }
      if (field === "phone") {
        if (!/^\+7\d{10}$/.test(value)) {
          return this.$t("Телефон должен быть в формате +7XXXXXXXXXX");
        }
      }
      if (field === "email") {
        if (!EMAIL_RE.test(value)) {
          return this.$t("Некорректный e-mail");
        }
      }
      if (field === "website") {
        if (!URL_RE.test(value)) {
          return this.$t("Ссылка должна начинаться с http:// или https://");
        }
      }
      if (field === "iik") {
        if (!/^[A-Z0-9]{20}$/.test(value)) {
          return this.$t("ИИК должен содержать 20 символов");
        }
      }
      return "";
    },
    validateAll() {
      const fields = [...REQUIRED_FIELDS, "website", "iik"];
      const next = {};
      for (const field of fields) {
        const message = this.getFieldError(field);
        if (message) next[field] = message;
      }
      this.errors = next;
      return Object.keys(next).length === 0;
    },
    async validate() {
      const valid = this.validateAll();
      if (!valid) {
        this.$nextTick(() => {
          const firstErrorField = Object.keys(this.errors)[0];
          if (firstErrorField) {
            const el = this.$el.querySelector(
              `[name="${firstErrorField}"], .input-error`
            );
            if (el && el.scrollIntoView) {
              el.scrollIntoView({ behavior: "smooth", block: "center" });
            }
          }
        });
      }
      return valid;
    },
    setServerErrors(detail) {
      if (!Array.isArray(detail)) return;
      const fieldErrors = {};
      for (const item of detail) {
        const loc = item?.loc || [];
        const field = loc[loc.length - 1];
        if (typeof field === "string") {
          fieldErrors[field] = item.msg || this.$t("Ошибка валидации");
        }
      }
      this.errors = { ...this.errors, ...fieldErrors };
    },
  },
};
</script>
<style scoped lang="scss">
.main {
  @apply flex items-start gap-5 self-stretch h-full overflow-y-auto;

  .left {
    @apply flex flex-col items-start gap-12 bg-card p-8 h-full overflow-y-auto;
    flex: 1 0 0;
    border-radius: 20px;
    border: 1px solid hsl(var(--border));
    background: hsl(var(--card));

    .content {
      @apply flex flex-col items-start gap-6 self-stretch h-[820px];

      .section {
        @apply flex flex-col items-start gap-3 w-full;

        .input-section {
          @apply flex flex-col items-end gap-3 w-full;
        }
      }
    }
  }

  .right {
    @apply h-full overflow-y-auto flex flex-col items-start gap-12 bg-card p-8;
    flex: 1 0 0;
    border-radius: 20px;
    border: 1px solid hsl(var(--border));
    background: hsl(var(--card));

    .content {
      @apply flex flex-col items-start gap-6 self-stretch;

      .right-block {
        @apply flex flex-col items-start gap-6 self-stretch;

        .blocked {
          @apply w-16 h-16 flex justify-center items-center rounded-xl shrink-0;
          background-color: hsl(var(--muted));
        }

        .title {
          @apply text-base font-semibold;
          color: hsl(var(--foreground));
        }

        .label {
          @apply text-xs;
          color: hsl(var(--muted-foreground));
        }
      }

      .section {
        @apply flex flex-col items-start gap-3 self-stretch;

        .info-label {
          @apply text-sm font-medium;
          color: hsl(var(--foreground));
        }
      }
    }
  }

  .title {
    @apply font-semibold text-base;
    color: hsl(var(--foreground));
  }

  .label {
    @apply text-sm font-medium;
    color: hsl(var(--muted-foreground));
  }
}

:deep([data-slot="form-label"].required)::after {
  content: "*";
  margin-left: 2px;
  color: hsl(var(--destructive));
  font-weight: 600;
}

:deep([data-slot="form-label"].label-error) {
  color: hsl(var(--destructive));
}

:deep(.input-error) {
  border-color: hsl(var(--destructive)) !important;
}

@media (max-width: 900px) {
  .main {
    @apply flex flex-col p-4 gap-6;

    .left,
    .right {
      @apply w-full p-4;
    }

    .left .content,
    .right .content {
      @apply h-auto;
    }
  }
}
</style>
